import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import type { TENANTSINTERFACE } from '@fitness-force/models';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DrawerModule } from 'primeng/drawer';
import { AuditTrailMiniComponent } from '@fitness-force/audit-trail';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { ToastMessageService } from '@fitness-force/api';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';
import { DataGridComponent, NoComponentComponent } from '@fitness-force/grid';
import { ActionsComponent } from '@fitness-force/grid-utility/action';
import { EntityStatusComponent } from '@fitness-force/shared';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { RippleModule } from 'primeng/ripple';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { BehaviorSubject } from 'rxjs';
import {
  CommunicationCategory,
  CommunicationCategoryGridRow,
  isCommunicationCategoryActive,
} from '../../models';
import { CommunicationCategoryService } from '../../services';
import { CommunicationCategoryFormComponent } from '../communication-category-form/communication-category-form.component';

@Component({
  selector: 'ff-communication-category-list',
  templateUrl: './communication-category-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [ToastMessageService, ConfirmationService],
  imports: [
    AsyncPipe,
    FormsModule,
    ButtonModule,
    ConfirmDialogModule,
    RippleModule,
    SkeletonModule,
    TableModule,
    InputTextModule,
    SelectModule,
    DataGridComponent,
    TranslateModule,
    BreadcrumbComponent,
    CommunicationCategoryFormComponent,
    IconFieldModule,
    InputIconModule,
    DrawerModule,
    AuditTrailMiniComponent,
  ],
})
export class CommunicationCategoryListComponent implements OnInit, OnDestroy {
  private readonly categoryService = inject(CommunicationCategoryService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly destroy$ = new Subject<void>();

  tenantId = 0;
  searchText = '';
  selectedStatusFilter = '';
  statusFilters = [
    { name: 'ADMIN.COMMON.ALL', value: '' },
    { name: 'ADMIN.COMMON.ACTIVE', value: 'active' },
    { name: 'ADMIN.COMMON.INACTIVE', value: 'inactive' },
  ];
  showAuditTrailSidebar = false;
  showAuditLogsFeature = false;
  allCommunicationCategories: CommunicationCategoryGridRow[] = [];

  showFormDialog = signal(false);
  activeCategory = signal<CommunicationCategory | null>(null);

  loading = new BehaviorSubject<boolean>(false);
  tableData = new BehaviorSubject<CommunicationCategoryGridRow[]>([]);
  columns = new BehaviorSubject<unknown[]>([]);
  totalRecordLength = new BehaviorSubject({
    totalPage: 1,
    totalItems: 0,
    currentPage: 1,
    reset: false,
  });
  emptyStateComponent$ = new BehaviorSubject<unknown>(NoComponentComponent);
  selectAllData = new BehaviorSubject(false);

  ngOnInit(): void {
    this.featureFlagService.getFlag$('show_audit_trial').pipe(takeUntil(this.destroy$)).subscribe((isEnabled: boolean) => {
      this.showAuditLogsFeature = isEnabled;
      this.cdr.detectChanges();
    });
    this.store.select(TENANTS_INFO).pipe(takeUntil(this.destroy$)).subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId = tenant?.selected_tenant?.id ?? 0;
    });
    this.buildColumns();
    this.loadList();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyFilters(): void {
    let filtered = [...this.allCommunicationCategories];
    if (this.searchText) {
      const lower = this.searchText.toLowerCase();
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(lower)
      );
    }
    if (this.selectedStatusFilter === 'active') {
      filtered = filtered.filter(c => isCommunicationCategoryActive(c.status));
    } else if (this.selectedStatusFilter === 'inactive') {
      filtered = filtered.filter(c => !isCommunicationCategoryActive(c.status));
    }
    this.tableData.next(filtered);
    this.totalRecordLength.next({
      totalPage: 1,
      totalItems: filtered.length,
      currentPage: 1,
      reset: false,
    });
  }

  onSearch(value: string): void {
    this.searchText = value;
    this.applyFilters();
  }

  onStatusFilterChange(value: string): void {
    this.selectedStatusFilter = value;
    this.applyFilters();
  }

  showAuditTrail(): void {
    if (this.showAuditLogsFeature) {
      this.showAuditTrailSidebar = true;
      this.cdr.detectChanges();
    }
  }

  hideAuditTrail(): void {
    this.showAuditTrailSidebar = false;
    this.cdr.detectChanges();
  }

  loadList(): void {
    this.loading.next(true);
    this.categoryService.listCategories().subscribe({
      next: (items) => {
        const rows = items.map((item) => this.mapRowForGrid(item));
        this.allCommunicationCategories = rows;
        this.loading.next(false);
        this.applyFilters();
        this.cdr.detectChanges();
      },
      error: (err: unknown) => {
        this.allCommunicationCategories = [];
        this.tableData.next([]);
        this.loading.next(false);
        this.toastMessageService.showError(err);
        this.cdr.detectChanges();
      },
    });
  }

  private mapRowForGrid(item: CommunicationCategory): CommunicationCategoryGridRow {
    return {
      ...item,
      typeLabel: item.isSystem
        ? this.translate.instant('COMMUNICATION_CATEGORIES.LIST.TYPE_SYSTEM')
        : this.translate.instant('COMMUNICATION_CATEGORIES.LIST.TYPE_CUSTOM'),
      rowActions: item.isSystem ? ['edit', 'status'] : ['edit', 'status', 'delete'],
    };
  }

  buildColumns(): void {
    this.translate
      .get([
        'COMMUNICATION_CATEGORIES.LIST.NAME',
        'COMMUNICATION_CATEGORIES.LIST.DESCRIPTION',
        'COMMUNICATION_CATEGORIES.LIST.TYPE',
        'COMMUNICATION_CATEGORIES.LIST.CODE',
        'ADMIN.COMMON.STATUS',
      ])
      .subscribe((t) => {
        this.columns.next([
          {
            title: t['COMMUNICATION_CATEGORIES.LIST.NAME'].toUpperCase(),
            classes: 'py-2 white-space-nowrap font-bold',
            sortable: true,
            filterable: true,
            dataProperty: 'name',
            type: 'default',
          },
          {
            title: t['COMMUNICATION_CATEGORIES.LIST.DESCRIPTION'].toUpperCase(),
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: true,
            dataProperty: 'description',
            type: 'default',
          },
          {
            title: t['COMMUNICATION_CATEGORIES.LIST.TYPE'].toUpperCase(),
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: true,
            dataProperty: 'typeLabel',
            type: 'default',
          },
          {
            title: t['COMMUNICATION_CATEGORIES.LIST.CODE'].toUpperCase(),
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: true,
            dataProperty: 'code',
            type: 'default',
          },
          {
            title: t['ADMIN.COMMON.STATUS'].toUpperCase(),
            classes: 'py-2 white-space-nowrap',
            sortable: false,
            filterable: false,
            dataProperty: 'status',
            type: 'component',
            data: { name: EntityStatusComponent },
          },
          {
            title: '',
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'actions',
            type: 'component',
            data: {
              name: ActionsComponent,
              additionData: true,
              aData: ['edit', 'status', 'delete'],
            },
          },
        ]);
        this.cdr.detectChanges();
      });
  }

  onActionEvent(event: { action: string; columnData: CommunicationCategoryGridRow }): void {
    const action = event.action as 'edit' | 'status' | 'delete';
    switch (action) {
      case 'edit':
        this.openEdit(event.columnData);
        break;
      case 'status':
        this.toggleStatus(event.columnData);
        break;
      case 'delete':
        this.confirmDelete(event.columnData);
        break;
      default: {
        const _exhaustive: never = action;
        return _exhaustive;
      }
    }
  }

  openCreate(): void {
    this.activeCategory.set(null);
    this.showFormDialog.set(true);
  }

  openEdit(category: CommunicationCategory): void {
    this.activeCategory.set(category);
    this.showFormDialog.set(true);
  }

  toggleStatus(category: CommunicationCategoryGridRow): void {
    const activate = !isCommunicationCategoryActive(category.status);
    const statusCall$ = activate
      ? this.categoryService.markActive(category.id)
      : this.categoryService.markInactive(category.id);

    statusCall$.subscribe({
      next: () => {
        const msgKey = activate
          ? 'COMMUNICATION_CATEGORIES.LIST.ACTIVATED_SUCCESS'
          : 'COMMUNICATION_CATEGORIES.LIST.DEACTIVATED_SUCCESS';
        this.translate.get(msgKey).subscribe((msg) => {
          this.toastMessageService.showSuccess(msg);
        });
        this.loadList();
      },
      error: (err: unknown) => {
        this.toastMessageService.showError(err);
      },
    });
  }

  confirmDelete(category: CommunicationCategoryGridRow): void {
    if (category.isSystem) {
      this.translate
        .get('COMMUNICATION_CATEGORIES.LIST.CANNOT_DELETE_SYSTEM')
        .subscribe((msg) => {
          this.toastMessageService.showError(msg);
        });
      return;
    }

    this.translate
      .get('COMMUNICATION_CATEGORIES.LIST.DELETE_CONFIRM')
      .subscribe((message) => {
        this.confirmationService.confirm({
          message,
          header: this.translate.instant('ADMIN.COMMON.DELETE'),
          icon: 'pi pi-exclamation-triangle',
          accept: () => this.deleteCategory(category),
        });
      });
  }

  private deleteCategory(category: CommunicationCategoryGridRow): void {
    this.categoryService.deleteCategory(category.id).subscribe({
      next: () => {
        this.translate
          .get('COMMUNICATION_CATEGORIES.LIST.DELETED_SUCCESS')
          .subscribe((msg) => {
            this.toastMessageService.showSuccess(msg);
          });
        this.loadList();
      },
      error: (err: unknown) => {
        this.toastMessageService.showError(err);
      },
    });
  }

  onFormSaved(): void {
    this.closeForm();
    this.loadList();
  }

  closeForm(): void {
    this.showFormDialog.set(false);
    this.activeCategory.set(null);
  }
}
